/**
 * AI difficulty tiers. (§10.3)
 *
 * The one rule from the brief that matters here: difficulty scales ONLY
 * reaction delay, decision quality (how many options are evaluated), error
 * magnitude, positioning discipline, and pressing aggression. It NEVER scales a
 * physical attribute — no extra speed, no boosted stats, no perfect knowledge.
 * "Players always detect stat-cheating and it destroys trust" (§10.2). Every
 * knob below is a cognitive/behavioural one, applied on top of the SAME player
 * controller and the SAME error models a human goes through.
 */

import {
  AI_REACTION_MAX_MS,
  AI_REACTION_MIN_MS,
} from '../sim/constants.js';
import { clamp } from '../math/vec3.js';

export type Difficulty = 'easy' | 'normal' | 'hard';

export interface DifficultyParams {
  /** Base reaction delay before the AI acts on a surprise (turnover, teleport). */
  reactionMs: number;
  /** ± jitter added to reaction, drawn from the AI's own rng (error injection). */
  reactionJitterMs: number;
  /** Extra reaction penalty when the stimulus is outside the vision cone (§10.2). */
  visionPenaltyMs: number;
  /** How many teammate pass options are scored — decision quality (§10.3). */
  passOptions: number;
  /** Std-dev (radians) of the extra aim error the AI injects on top of the sim's. */
  aimErrorRad: number;
  /** How many defenders press the ball carrier; the rest hold shape (§10.3). */
  pressingCount: number;
  /** How far out a defender will step to engage the carrier. */
  pressRadius: number;
  /** Max distance from goal at which the AI will attempt a shot. */
  shootRange: number;
  /** Whether this tier commits to slide tackles (a red-card risk if mistimed). */
  slideEnabled: boolean;
  /**
   * Positioning discipline 0..1 — how tightly a player holds its formation
   * anchor vs. ball-chasing. Higher = more disciplined shape.
   */
  discipline: number;
  /**
   * 0..1 chance PER ENGAGE DECISION of committing a standing tackle a beat
   * before the AI's own cautious pre-check (AI_TACKLE_COMMIT_MARGIN) would
   * allow it — the "lunge" (CR-27/CR-29, owner: bots "just look at it from
   * far, they dont try to slide tackle"). AI_TACKLE_COMMIT_MARGIN's own doc
   * anticipated this exact knob: "an easy AI diving in and a hard AI timing
   * it is a real idea... it belongs in ai/difficulty.ts". A defender never
   * commits on geometry the REFEREE would call an outright foul (the lunge
   * gate reuses reachesBallBeforeMan at requiredExtraMargin=0 — the SAME
   * predicate resolveTackle itself applies, just without the AI's usual
   * travel-time safety buffer), so the risk is real but not reckless: the
   * ~75-107ms before contact can still turn a marginal-but-fair lunge into a
   * foul if the carrier moves. Zero on easy — a beginner-tier defender does
   * not gamble.
   */
  tackleLungeChance: number;
}

/**
 * The tuning table. These are deliberately conservative: an AI that is "too
 * easy" is fixed by improving decisions, never by cranking a number past what a
 * human could do (§10.2). Reaction values stay inside [AI_REACTION_MIN_MS,
 * AI_REACTION_MAX_MS] after jitter+clamp so no tier ever reacts superhumanly.
 */
export const DIFFICULTY_PARAMS: Record<Difficulty, DifficultyParams> = {
  easy: {
    reactionMs: 330,
    reactionJitterMs: 40,
    visionPenaltyMs: 120,
    passOptions: 3,
    aimErrorRad: 0.14,
    pressingCount: 1,
    pressRadius: 10,
    shootRange: 18,
    slideEnabled: false,
    discipline: 0.45,
    tackleLungeChance: 0,
  },
  normal: {
    reactionMs: 250,
    reactionJitterMs: 40,
    visionPenaltyMs: 90,
    passOptions: 6,
    aimErrorRad: 0.075,
    pressingCount: 2,
    pressRadius: 14,
    shootRange: 22,
    // ⚠ WAS false. Normal is the default difficulty — with slides off, the
    // vast majority of players NEVER see a bot slide-tackle at all, only the
    // standing-tackle "lunge" above. Owner, 2026-08-23: "make sure the bots
    // slide" — the same "they dont try to slide tackle" complaint this file's
    // own tackleLungeChance doc already anticipated, just not yet acted on
    // for this tier. Easy stays false on purpose (§ "a beginner-tier defender
    // does not gamble", consistent with its tackleLungeChance: 0).
    slideEnabled: true,
    discipline: 0.65,
    tackleLungeChance: 0.08,
  },
  hard: {
    reactionMs: 165,
    reactionJitterMs: 30,
    visionPenaltyMs: 55,
    passOptions: 12,
    aimErrorRad: 0.03,
    pressingCount: 3,
    pressRadius: 18,
    shootRange: 26,
    slideEnabled: true,
    discipline: 0.8,
    tackleLungeChance: 0.18,
  },
};

/** Convert a millisecond delay to whole sim ticks, clamped to the legal band. */
export function reactionMsToTicks(ms: number, tickRate: number): number {
  const clamped = clamp(ms, AI_REACTION_MIN_MS, AI_REACTION_MAX_MS);
  // Round to nearest tick; a sub-tick delay still costs at least one tick.
  const ticks = Math.round((clamped / 1000) * tickRate);
  return ticks < 1 ? 1 : ticks;
}
